import { memo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check } from "lucide-react";
import { cn } from "../../lib/cn";
import { ArtifactBlock } from "./ArtifactBlock";

/**
 * Полноценный markdown-рендер для assistant-сообщений.
 * Поддерживает GFM: таблицы, чеклисты, strikethrough, autolinks.
 * Code fences рендерятся через `<pre><code>` с copy-кнопкой.
 */
export const StreamingText = memo(function StreamingText({
  text,
  streaming,
  className,
}: {
  text: string;
  streaming?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("text-body leading-relaxed prose-content", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Таблицы — премиум-стиль с скруглениями и тонкими границами.
          table: ({ children, ...props }) => (
            <div className="my-3 overflow-x-auto rounded-card border border-border/60">
              <table
                {...props}
                className="w-full border-collapse text-small"
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead {...props} className="bg-bg-subtle">
              {children}
            </thead>
          ),
          th: ({ children, ...props }) => (
            <th
              {...props}
              className="px-3 py-2 text-left font-semibold text-fg border-b border-border/60"
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td {...props} className="px-3 py-2 border-b border-border/30 last:border-b-0">
              {children}
            </td>
          ),
          tr: ({ children, ...props }) => (
            <tr {...props} className="even:bg-bg-subtle/30">
              {children}
            </tr>
          ),

          // Списки + GFM task list (план как чеклист)
          ul: ({ children, className: cls, ...props }) => (
            <ul
              {...props}
              className={cn(
                cls?.includes("contains-task-list") ? "list-none" : "list-disc",
                "ml-5 my-2 space-y-1",
              )}
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol {...props} className="list-decimal ml-5 my-2 space-y-1">
              {children}
            </ol>
          ),
          li: ({ children, className: cls, ...props }) => (
            <li
              {...props}
              className={cn(
                cls?.includes("task-list-item") ? "flex items-start gap-2 -ml-5" : "",
                "leading-relaxed",
              )}
            >
              {children}
            </li>
          ),
          input: ({ type, checked, ...props }) => {
            if (type === "checkbox") {
              // Чеклист-чекбокс: показываем как иконку статуса (готово / открыто).
              return (
                <span
                  className={cn(
                    "inline-flex items-center justify-center size-4 mt-0.5 rounded-sm border shrink-0",
                    checked
                      ? "bg-accent-500 border-accent-500 text-white"
                      : "border-border bg-bg-card",
                  )}
                  aria-checked={checked}
                  role="checkbox"
                >
                  {checked && (
                    <svg viewBox="0 0 16 16" className="size-3" fill="currentColor">
                      <path d="M13.78 4.22a.75.75 0 010 1.06l-7 7a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 111.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 011.06 0z" />
                    </svg>
                  )}
                </span>
              );
            }
            return <input type={type} {...props} />;
          },

          // Code blocks.
          // react-markdown v9 убрал prop `inline`. Inline-код определяем сами:
          // у блочного code есть className `language-*` ИЛИ многострочное содержимое.
          // Иначе это inline-спан (`имя_файла`, `add`) — рендерим компактно, БЕЗ
          // обёртки CodeBlock (иначе получается артефакт «code / копировать / имя»).
          code: ({ className: cls, children, ...props }: any) => {
            const raw = String(children ?? "");
            const hasLang = /language-/.test(cls || "");
            const isBlock = hasLang || raw.includes("\n");
            if (!isBlock) {
              return (
                <code
                  {...props}
                  className="px-1 py-0.5 rounded bg-bg-subtle text-fg font-mono text-[0.92em] border border-border/60"
                >
                  {children}
                </code>
              );
            }
            const lang = (cls || "").replace("language-", "").toLowerCase();
            // HTML / SVG / Mermaid → ArtifactBlock с preview.
            if (lang === "html" || lang === "svg" || lang === "mermaid") {
              const code = String(children).replace(/\n$/, "");
              return <ArtifactBlock language={lang} code={code} />;
            }
            const codeText = String(children).replace(/\n$/, "");
            return <CodeBlock lang={lang} code={codeText} cls={cls} />;
          },
          pre: ({ children }) => <>{children}</>,

          // Headings
          h1: ({ children }) => <h1 className="text-h1 mt-3 mb-2 font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="text-h2 mt-3 mb-2 font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="text-h3 mt-2 mb-1.5 font-semibold">{children}</h3>,

          // Inline elements
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ children, ...props }) => (
            <a
              {...props}
              className="text-accent-400 hover:text-accent-300 underline underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-accent-500/30 pl-4 my-2 italic text-fg-muted">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-border/40" />,
          p: ({ children }) => <p className="my-1.5">{children}</p>,
        }}
      >
        {text}
      </ReactMarkdown>
      {streaming && (
        <span
          className="inline-block w-2 h-4 align-text-bottom ml-0.5 bg-accent rounded-sm animate-pulse"
          aria-hidden
        />
      )}
    </div>
  );
});

/** Кодовый блок с copy-кнопкой и подписью языка. */
function CodeBlock({ lang, code, cls }: { lang: string; code: string; cls?: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div className="my-2 group/code relative rounded-card bg-bg-subtle border border-border/70 overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50 bg-bg-card/40">
        <span className="text-[10px] font-mono uppercase tracking-wider text-fg-subtle">
          {lang || "code"}
        </span>
        <button
          type="button"
          onClick={copy}
          className="opacity-60 group-hover/code:opacity-100 transition-opacity inline-flex items-center gap-1 text-[11px] text-fg-muted hover:text-fg px-1.5 py-0.5 rounded hover:bg-bg-subtle"
          aria-label="Скопировать код"
          title="Скопировать"
        >
          {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
          <span className="hidden sm:inline">{copied ? "скопировано" : "копировать"}</span>
        </button>
      </div>
      <pre className="p-3 overflow-x-auto font-mono text-small">
        <code className={cls}>{code}</code>
      </pre>
    </div>
  );
}
